import { useEffect, useState } from 'react';
import { useSearch } from '@tanstack/react-router';
import { Button, Textarea } from '@orion/ui';
import { PageHeader } from '../../../app/shell.js';
import { ApiError } from '../../../lib/api-client.js';
import { useAuth } from '../../../lib/auth.js';
import { useToast } from '../../../lib/toast.js';
import { useNotificationsQuery } from '../../notifications/api.js';
import { PulseIcon } from './PulseIcon.js';
import { ConnectImapDialog } from './ConnectImapDialog.js';
import { PulseConnectionCard } from './PulseConnectionCard.js';
import { PulseAllConnectionsView } from './PulseAllConnectionsView.js';
import { pulseConnectUrl, usePulseConnectionsQuery, usePulseSignatureQuery, useUpdatePulseSignature } from './api.js';

const CONNECT_ERROR_MESSAGE: Record<string, string> = {
  not_configured: "This workspace isn't set up for that provider yet — ask an admin to add the OAuth credentials.",
  state_mismatch: 'That connection attempt expired or was tampered with. Try connecting again.',
  connect_failed: "Something went wrong finishing the connection. If this keeps happening, try removing Orion's access from your Google/Microsoft account settings first, then reconnect.",
  insufficient_scope:
    "Google connected the account but didn't grant Gmail access — on the consent screen, make sure every requested permission is checked (especially the one covering reading, sending, and managing your email), then try again.",
  active_elsewhere: 'That mailbox is already actively connected under a different account — have them disconnect it first, or connect a different mailbox.',
};
const MINE_SEARCH_THRESHOLD = 5;
const MINE_PAGE_SIZE = 10;
const CONNECTED_PROVIDER_LABEL: Record<string, string> = { google: 'Google Workspace', microsoft: 'Microsoft 365' };

/** Appended to every reply sent via a lead's Email tab (PulseReplyService)
 * — personal, like the mailbox connections below. */
function SignatureCard() {
  const { data: signature, isLoading } = usePulseSignatureQuery();
  const updateSignature = useUpdatePulseSignature();
  const toast = useToast();
  const [text, setText] = useState('');

  useEffect(() => {
    if (signature) setText(signature.signature ?? '');
  }, [signature]);

  const dirty = signature && text.trim() !== (signature.signature ?? '');

  const save = async () => {
    try {
      await updateSignature.mutateAsync(text);
      toast.success('Signature saved.');
    } catch (err) {
      const detail = err instanceof ApiError ? (err.problem.detail ?? err.problem.title) : 'Failed to save.';
      toast.error(detail);
    }
  };

  if (isLoading) return null;

  return (
    <section className="flex max-w-xl flex-col gap-3 rounded-dialog border border-border bg-surface p-5 shadow-card">
      <div className="flex flex-col gap-0.5">
        <h2 className="text-[15px] font-semibold leading-5">Your signature</h2>
        <span className="text-[13px] text-text-muted">Appended to every reply you send from a lead&apos;s Email tab.</span>
      </div>
      <Textarea
        label="Signature"
        hideLabel
        rows={4}
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder={'Jordan Reyes\nAccount Executive, OES'}
      />
      <Button size="sm" className="self-start" onClick={save} loading={updateSignature.isPending} disabled={!dirty}>
        Save signature
      </Button>
    </section>
  );
}

/** Admin > Integrations > Inbox Pulse — "Your inbox, alive in the CRM." */
export function InboxPulsePage() {
  const search = useSearch({ from: '/_app/admin/integrations/inbox-pulse' }) as { connected?: string; error?: string };
  const { data: connections, isLoading } = usePulseConnectionsQuery();
  const { data: unreadNotifications } = useNotificationsQuery(true);
  const { hasPermission } = useAuth();
  const [showConnectOptions, setShowConnectOptions] = useState(false);
  const [showImapDialog, setShowImapDialog] = useState(false);
  const [tab, setTab] = useState<'mine' | 'all'>('mine');
  const [mineSearch, setMineSearch] = useState('');
  const [mineVisible, setMineVisible] = useState(MINE_PAGE_SIZE);
  const canSeeAllConnections = hasPermission('admin.integrations.manage');
  const mineQuery = mineSearch.trim().toLowerCase();
  const filteredMine = (connections ?? []).filter((c) => !mineQuery || c.emailAddress.toLowerCase().includes(mineQuery));
  const visibleMine = filteredMine.slice(0, mineVisible);
  const breadcrumb = [{ label: 'Administration', to: '/admin' }, { label: 'Inbox Pulse' }];
  // The spec's "amber dot ... when there are unread captures" — reuses the
  // generic unread-notifications list rather than a Pulse-specific
  // endpoint, filtered to this feature's own notification types.
  const hasUnreadCaptures = (unreadNotifications?.data ?? []).some((n) => n.type === 'pulse.lead_created' || n.type === 'pulse.reply_received');

  return (
    <>
      <PageHeader breadcrumb={breadcrumb} />
      <main className="flex flex-col gap-5 px-8 py-7">
        <div className="flex items-start justify-between gap-6">
          <div className="flex flex-col gap-1">
            <h1 className="text-[22px] font-[650] leading-7 tracking-[-0.01em]">Inbox Pulse</h1>
            <p className="text-sm leading-5 text-text-muted">Your inbox, alive in the CRM.</p>
          </div>
          {tab === 'mine' && (
            <Button size="md" onClick={() => setShowConnectOptions((v) => !v)}>
              Connect mailbox
            </Button>
          )}
        </div>

        <div className="flex items-start gap-3 rounded-dialog border border-border bg-surface p-5 shadow-card">
          <span className="flex size-10 shrink-0 items-center justify-center rounded-control border border-border bg-surface-subtle">
            <PulseIcon size={22} unread={hasUnreadCaptures} />
          </span>
          <div className="flex flex-col gap-0.5">
            <h2 className="text-[15px] font-semibold leading-5">Inbox Pulse</h2>
            <span className="text-[13px] text-text-muted">Turns replies to your outreach into CRM leads automatically — Google Workspace, Microsoft 365, or any IMAP mailbox.</span>
          </div>
        </div>

        {canSeeAllConnections && (
          <div className="flex items-center gap-5 border-b border-border">
            <button
              type="button"
              onClick={() => setTab('mine')}
              className={`pb-2.5 text-[13px] font-semibold ${tab === 'mine' ? 'border-b-2 border-primary text-ink' : 'border-b-2 border-transparent font-medium text-text-muted hover:text-ink'}`}
            >
              My mailbox
            </button>
            <button
              type="button"
              onClick={() => setTab('all')}
              className={`pb-2.5 text-[13px] font-semibold ${tab === 'all' ? 'border-b-2 border-primary text-ink' : 'border-b-2 border-transparent font-medium text-text-muted hover:text-ink'}`}
            >
              All connections
            </button>
          </div>
        )}

        {tab === 'mine' ? (
          <>
            {search.error && (
              <div role="alert" className="max-w-xl rounded-control bg-danger-soft px-3 py-2 text-sm font-medium text-danger">
                {CONNECT_ERROR_MESSAGE[search.error] ?? 'Something went wrong connecting that mailbox.'}
              </div>
            )}
            {search.connected && !search.error && (
              <div role="status" className="max-w-xl rounded-control bg-success-soft px-3 py-2 text-sm font-medium text-success">
                {CONNECTED_PROVIDER_LABEL[search.connected] ?? search.connected} connected.
              </div>
            )}

            {showConnectOptions && (
              <div className="flex max-w-xl flex-col gap-2 rounded-dialog border border-border bg-surface p-4 shadow-card">
                <a
                  href={pulseConnectUrl('google')}
                  className="flex items-center justify-between rounded-control border border-border-strong px-4 py-3 text-sm font-medium text-ink hover:bg-surface-subtle"
                >
                  Connect Google Workspace (Gmail API)
                  <span className="text-text-muted">&rarr;</span>
                </a>
                <a
                  href={pulseConnectUrl('microsoft')}
                  className="flex items-center justify-between rounded-control border border-border-strong px-4 py-3 text-sm font-medium text-ink hover:bg-surface-subtle"
                >
                  Connect Microsoft 365 (Graph API)
                  <span className="text-text-muted">&rarr;</span>
                </a>
                <button
                  type="button"
                  onClick={() => setShowImapDialog(true)}
                  className="flex items-center justify-between rounded-control border border-border-strong px-4 py-3 text-left text-sm font-medium text-ink hover:bg-surface-subtle"
                >
                  Connect via IMAP + App Password
                  <span className="text-text-muted">&rarr;</span>
                </button>
              </div>
            )}

            <SignatureCard />

            {!isLoading && (connections ?? []).length === 0 && (
              <div className="max-w-xl rounded-control border border-dashed border-border bg-surface-subtle px-4 py-4 text-[13px] text-text-muted">
                No mailbox connected yet — use &ldquo;Connect mailbox&rdquo; above to get started.
              </div>
            )}

            <div className="flex max-w-xl flex-col gap-4">
              {(connections ?? []).length > MINE_SEARCH_THRESHOLD && (
                <input
                  value={mineSearch}
                  onChange={(e) => {
                    setMineSearch(e.target.value);
                    setMineVisible(MINE_PAGE_SIZE);
                  }}
                  type="search"
                  placeholder="Search your mailboxes…"
                  className="h-9 rounded-control border border-border-strong bg-surface px-3 text-sm"
                />
              )}
              {visibleMine.map((c) => (
                <PulseConnectionCard key={c.id} connection={c} collapsible={(connections ?? []).length > 1} />
              ))}
              {filteredMine.length > mineVisible && (
                <Button size="sm" variant="secondary" className="self-start" onClick={() => setMineVisible((n) => n + MINE_PAGE_SIZE)}>
                  Show {Math.min(MINE_PAGE_SIZE, filteredMine.length - mineVisible)} more ({filteredMine.length - mineVisible} remaining)
                </Button>
              )}
              {mineSearch.trim() && filteredMine.length === 0 && <span className="text-[13px] text-text-muted">No mailboxes match.</span>}
            </div>
          </>
        ) : (
          <PulseAllConnectionsView />
        )}
      </main>

      {showImapDialog && <ConnectImapDialog onClose={() => setShowImapDialog(false)} />}
    </>
  );
}
